import {
  CsSubmitError,
  type CsSubmitErrorKind,
  type SubmitTicketRequest,
  type SubmitTicketResponse,
  type UploadTicketAssetResponse,
} from './types';
import { trimTrailingSlash } from './url';

export interface CsClientConfig {
  /** CRM API base URL, e.g. `https://crm-dev.zep.us`. Trailing slash optional. */
  baseUrl: string;
  /** Extra headers merged onto every request (auth, trace IDs, etc.). */
  headers?: CsRequestHeadersInit;
}

export interface RequestOptions {
  /** Propagated to `fetch` — lets callers cancel mid-flight (e.g. on unmount). */
  signal?: AbortSignal;
}

type CsRequestHeadersInit = NonNullable<Parameters<typeof fetch>[1]>['headers'];
type CsRequestBodyInit = NonNullable<
  NonNullable<Parameters<typeof fetch>[1]>['body']
>;

export interface CsClient {
  submitTicket(
    req: SubmitTicketRequest,
    options?: RequestOptions,
  ): Promise<SubmitTicketResponse>;
  uploadTicketAsset(
    file: File,
    options?: RequestOptions,
  ): Promise<UploadTicketAssetResponse>;
}

export function createCsClient(config: CsClientConfig): CsClient {
  return {
    submitTicket: (req, options) => submitTicket(config, req, options),
    uploadTicketAsset: (file, options) =>
      uploadTicketAsset(config, file, options),
  };
}

export async function uploadTicketAsset(
  config: CsClientConfig,
  file: File,
  options?: RequestOptions,
): Promise<UploadTicketAssetResponse> {
  const formData = new FormData();
  formData.append('file', file);

  return request(config, {
    path: '/api/public/cs/ticket-assets',
    headers: config.headers,
    body: formData,
    signal: options?.signal,
    errorLabel: 'CS ticket asset upload',
    validate: isUploadTicketAssetResponse,
  });
}

export async function submitTicket(
  config: CsClientConfig,
  req: SubmitTicketRequest,
  options?: RequestOptions,
): Promise<SubmitTicketResponse> {
  return request(config, {
    path: '/api/public/cs/tickets',
    headers: { 'Content-Type': 'application/json', ...config.headers },
    body: JSON.stringify(req),
    signal: options?.signal,
    errorLabel: 'CS ticket submission',
    validate: isSubmitTicketResponse,
  });
}

interface RequestConfig<T> {
  path: string;
  headers?: CsRequestHeadersInit;
  body: CsRequestBodyInit;
  signal?: AbortSignal;
  errorLabel: string;
  validate: (value: unknown) => value is T;
}

async function request<T>(
  config: CsClientConfig,
  requestConfig: RequestConfig<T>,
): Promise<T> {
  const url = `${trimTrailingSlash(config.baseUrl)}${requestConfig.path}`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: requestConfig.headers,
      body: requestConfig.body,
      signal: requestConfig.signal,
    });
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    throw new CsSubmitError(
      aborted
        ? `${requestConfig.errorLabel} aborted`
        : `${requestConfig.errorLabel} failed: network error`,
      {
        status: 0,
        kind: aborted ? 'aborted' : 'network',
        body: err instanceof Error ? err.message : err,
      },
    );
  }

  // Read the body regardless of status so error responses carry context.
  const parsed = await readJsonOrText(response);

  if (!response.ok) {
    throw new CsSubmitError(
      `${requestConfig.errorLabel} failed with HTTP ${response.status}`,
      {
        status: response.status,
        kind: mapStatusToKind(response.status),
        body: parsed,
      },
    );
  }

  if (!requestConfig.validate(parsed)) {
    throw new CsSubmitError(
      `${requestConfig.errorLabel} succeeded but response shape was unexpected`,
      { status: response.status, kind: 'unknown', body: parsed },
    );
  }

  return parsed;
}

function mapStatusToKind(status: number): CsSubmitErrorKind {
  if (status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status === 502) return 'upstream';
  if (status >= 500) return 'server';
  return 'unknown';
}

async function readJsonOrText(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function isSubmitTicketResponse(value: unknown): value is SubmitTicketResponse {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return typeof item.ticket_id === 'string';
}

function isUploadTicketAssetResponse(
  value: unknown,
): value is UploadTicketAssetResponse {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.name === 'string' &&
    typeof item.url === 'string' &&
    typeof item.key === 'string' &&
    typeof item.size === 'number' &&
    typeof item.content_type === 'string'
  );
}
